import { Text } from "@certn/cascadia";
import { StatusTag } from "@certn/cascadia-react";
import type {
  CascadiaColor,
  CascadiaStandardIcon,
} from "@certn/cascadia-tokens";
import { useTranslation } from "react-i18next";

import type {
  LastRunStatusValue,
  MonitorStatusLabelKey,
  RunResultValue,
} from "../monitoringEnums";

interface TagAppearance {
  leftIcon: CascadiaStandardIcon;
  fill: CascadiaColor;
  color: CascadiaColor;
}

const NEUTRAL_FILL: Pick<TagAppearance, "fill" | "color"> = {
  fill: "background-accent-neutral-medium",
  color: "text-base",
};

const NEUTRAL_TAG: TagAppearance = { leftIcon: "remove", ...NEUTRAL_FILL };

export const MONITOR_STATUS_APPEARANCE: Record<
  MonitorStatusLabelKey,
  TagAppearance
> = {
  ACTIVE: {
    leftIcon: "check_circle",
    fill: "background-accent-blue-weak",
    color: "text-accent-blue-strong",
  },
  ENROLLED: { leftIcon: "check_circle", ...NEUTRAL_FILL },
  PAUSED: { leftIcon: "pause", ...NEUTRAL_FILL },
  RESUMED: { leftIcon: "check_circle", ...NEUTRAL_FILL },
  ENDED: { leftIcon: "schedule", ...NEUTRAL_FILL },
  DEACTIVATED: { leftIcon: "do_not_disturb_on", ...NEUTRAL_FILL },
  NOT_ENROLLED: { leftIcon: "do_not_disturb_on", ...NEUTRAL_FILL },
};

export const RUN_RESULT_APPEARANCE: Record<RunResultValue, TagAppearance> = {
  NO_CHANGE: {
    leftIcon: "remove",
    fill: "background-accent-green-weak",
    color: "text-accent-green-strong",
  },
  CHANGE_DETECTED: {
    leftIcon: "warning",
    fill: "background-accent-orange-weak",
    color: "text-accent-orange-strong",
  },
  UPCOMING_CHANGE: {
    leftIcon: "schedule",
    fill: "background-accent-orange-weak",
    color: "text-accent-orange-strong",
  },
  REQUIRES_ATTENTION: {
    leftIcon: "error",
    fill: "background-accent-red-weak",
    color: "text-accent-red-strong",
  },
  FAILED_TO_RUN: { leftIcon: "sentiment_dissatisfied", ...NEUTRAL_FILL },
};

export function useMonitorStatusLabels(): Record<
  MonitorStatusLabelKey,
  string
> {
  const { t } = useTranslation();
  return {
    ACTIVE: t("Active"),
    ENROLLED: t("Enrolled"),
    PAUSED: t("Paused"),
    RESUMED: t("Resumed"),
    ENDED: t("Ended"),
    DEACTIVATED: t("Deactivated"),
    NOT_ENROLLED: t("Not enrolled"),
  };
}

export function useRunResultLabels(): Record<RunResultValue, string> {
  const { t } = useTranslation();
  return {
    NO_CHANGE: t("No change"),
    CHANGE_DETECTED: t("Change detected"),
    UPCOMING_CHANGE: t("Upcoming change"),
    REQUIRES_ATTENTION: t("Require attention"),
    FAILED_TO_RUN: t("Failed to run"),
  };
}

export function useLastRunStatusLabels(): Record<LastRunStatusValue, string> {
  const { t } = useTranslation();
  return {
    SUCCESS: t("Success"),
    FAILED: t("Failed"),
    PENDING: t("Pending"),
  };
}

function tagTestId(prefix: string, value: string): string {
  return `${prefix}-${value.toLowerCase().replace(/_/g, "-")}`;
}

function isMonitorStatusLabelKey(
  status: string
): status is MonitorStatusLabelKey {
  return status in MONITOR_STATUS_APPEARANCE;
}

function isRunResultValue(result: string): result is RunResultValue {
  return result in RUN_RESULT_APPEARANCE;
}

function isLastRunStatusValue(status: string): status is LastRunStatusValue {
  return status === "SUCCESS" || status === "FAILED" || status === "PENDING";
}

export { isLastRunStatusValue, isMonitorStatusLabelKey, isRunResultValue };

export function MonitorStatusTag({ status }: { status: string | null }) {
  const labels = useMonitorStatusLabels();
  if (!status) {
    return <Text style="form/body-secondary">—</Text>;
  }
  const appearance = isMonitorStatusLabelKey(status)
    ? MONITOR_STATUS_APPEARANCE[status]
    : NEUTRAL_TAG;
  return (
    <StatusTag
      {...appearance}
      statusInfoIcon={false}
      testID={tagTestId("status", status)}
    >
      {isMonitorStatusLabelKey(status) ? labels[status] : status}
    </StatusTag>
  );
}

export function RunResultTag({ result }: { result: string | null }) {
  const labels = useRunResultLabels();
  if (!result) {
    return <Text style="form/body-secondary">—</Text>;
  }
  const appearance = isRunResultValue(result)
    ? RUN_RESULT_APPEARANCE[result]
    : NEUTRAL_TAG;
  return (
    <StatusTag
      {...appearance}
      statusInfoIcon={false}
      testID={tagTestId("result", result)}
    >
      {isRunResultValue(result) ? labels[result] : result}
    </StatusTag>
  );
}
